import {
  FC,
  useState,
  useRef,
  FocusEvent,
  KeyboardEvent,
  ChangeEvent,
} from 'react';
import s from './AssetsList.module.scss';
import cn from 'classnames';
import dayjs from 'dayjs';
import { Table, Sheet, Checkbox } from '@mui/joy';
import { TAsset } from '../../../../config/requestTypes';
import { truncateMiddle } from '../../../../utils/strings';
import copy from 'copy-to-clipboard';
import { EditableString } from '../EditableString/EditableString';

type TEditField = {
  id: string;
  fieldType: string;
};

interface AssetsListProps {
  assetsList: TAsset[];
  onSelect?: (id: string) => void;
  onUploadIcon?: (id: string, file: File) => void;
  onChangeString?: (id: string, string: string, type: string) => void;
  turnOnOff?: (id: string, e: ChangeEvent<HTMLInputElement>) => void;
}

const AssetsList: FC<AssetsListProps> = ({
  assetsList,
  onSelect,
  onUploadIcon,
  onChangeString,
  turnOnOff,
}) => {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [currentAssetId, setCurrentAssetId] = useState<string | null>(null);
  const [editField, setEditField] = useState<TEditField[]>([]);

  const handleCopy = (id: string) => {
    if (copy(id)) {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1000); // return regular color after 1 sec.
    }
  };

  const handleIconClick = (assetId: string) => {
    setCurrentAssetId(assetId);
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentAssetId) return;

    // Check file is svg
    if (file.type !== 'image/svg+xml') {
      alert('Пожалуйста, выберите файл в формате SVG!');
      return;
    }

    if (onUploadIcon) {
      onUploadIcon(currentAssetId, file); // call a function to upload icon
    }

    // Reset the input so that you can select the same file again.
    e.target.value = '';
  };

  const onDoubleClickEditableString = (asset: TAsset, fieldType: string) => {
    setEditField((prev) => [
      ...(prev ?? []),
      {
        id: asset.id,
        fieldType,
        value: asset[fieldType as keyof typeof asset],
      },
    ]);
  };

  const onSaveEditField = (
    id: string,
    e:
      | FocusEvent<HTMLInputElement | HTMLTextAreaElement>
      | KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    fieldType: string,
  ) => {
    setEditField([]);
    if (onChangeString) onChangeString(id, e.currentTarget.value, fieldType);
  };

  return (
    <Sheet>
      <Table>
        <thead>
          <tr>
            {onSelect && <th style={{ width: '50px' }} />}
            {turnOnOff && <th style={{ width: '78px' }}>Включен</th>}
            <th style={{ width: '70px' }}>ID</th>
            <th style={{ width: '68px' }}>Иконка</th>
            <th style={{ width: '130px' }}>Название</th>
            <th style={{ width: '110px' }}>Подзаголовок</th>
            <th>Описание</th>
            <th style={{ width: '60px' }}>Тип</th>
            <th style={{ width: '80px' }}>Код</th>
            <th style={{ width: '100px' }}>Комиссия %</th>
            <th style={{ width: '114px' }}>Кол-во знаков</th>
            <th style={{ width: '175px' }}>Создано / Обновлено</th>
          </tr>
        </thead>
        <tbody>
          {assetsList?.map((asset) => (
            <tr key={asset.id}>
              {onSelect && (
                <td className={s.table__td}>
                  <Checkbox size="sm" onChange={() => onSelect(asset.id)} />
                </td>
              )}
              {turnOnOff && (
                <td className={s.table__td}>
                  <Checkbox
                    size="sm"
                    defaultChecked={!asset.isHidden}
                    onChange={(e) => turnOnOff(asset.id, e)}
                  />
                </td>
              )}
              <td className={s.table__td}>
                <span
                  className={cn('user-select-none', {
                    copied: copiedId === asset.id,
                  })}
                  onClick={() => asset.id && handleCopy(String(asset.id))}
                  style={{ cursor: 'pointer' }}
                >
                  {truncateMiddle(asset.id, 3, 3)}
                </span>
                {copiedId === asset.id && (
                  <span className="copiedTooltip">Copied!</span>
                )}
              </td>
              <td className={s.table__td}>
                <span
                  onClick={() => handleIconClick(asset.id)}
                  style={{ cursor: 'pointer' }}
                  className="user-select-none"
                >
                  <img
                    src={asset.imageUrl}
                    className={s.table__assetList__icon}
                    alt="asset-icon"
                  />
                </span>
              </td>
              <td className={s.table__td}>
                <EditableString
                  id={asset.id}
                  stringType="title"
                  value={asset.title}
                  onDoubleClick={() =>
                    onDoubleClickEditableString(asset, 'title')
                  }
                  onBlurField={onSaveEditField}
                  onEnter={onSaveEditField}
                  onEscape={() => setEditField([])}
                  isEditable={
                    !!editField.find(
                      (a) => a.id === asset.id && a.fieldType === 'title',
                    )
                  }
                />
              </td>
              <td className={s.table__td}>
                <EditableString
                  id={asset.id}
                  stringType="subtitle"
                  value={asset.subtitle}
                  onDoubleClick={() =>
                    onDoubleClickEditableString(asset, 'subtitle')
                  }
                  onBlurField={onSaveEditField}
                  onEnter={onSaveEditField}
                  onEscape={() => setEditField([])}
                  isEditable={
                    !!editField.find(
                      (a) => a.id === asset.id && a.fieldType === 'subtitle',
                    )
                  }
                />
              </td>
              <td className={s.table__td}>
                <EditableString
                  id={asset.id}
                  stringType="description"
                  value={asset.description}
                  onDoubleClick={() =>
                    onDoubleClickEditableString(asset, 'description')
                  }
                  onBlurField={onSaveEditField}
                  onEnter={onSaveEditField}
                  onEscape={() => setEditField([])}
                  isEditable={
                    !!editField.find(
                      (a) => a.id === asset.id && a.fieldType === 'description',
                    )
                  }
                  isTextarea
                />
              </td>
              <td className={s.table__td}>{asset.type}</td>
              <td className={s.table__td}>{asset.symbol}</td>
              <td className={s.table__td}>
                <EditableString
                  id={asset.id}
                  stringType="commissionPercent"
                  value={asset.commissionPercent}
                  onDoubleClick={() =>
                    onDoubleClickEditableString(asset, 'commissionPercent')
                  }
                  onBlurField={onSaveEditField}
                  onEnter={onSaveEditField}
                  onEscape={() => setEditField([])}
                  isEditable={
                    !!editField.find(
                      (a) =>
                        a.id === asset.id &&
                        a.fieldType === 'commissionPercent',
                    )
                  }
                  onlyNumber
                  numberStep={0.01}
                />
              </td>
              <td className={s.table__td}>
                <EditableString
                  id={asset.id}
                  stringType="decimalPlaces"
                  value={String(asset.decimalPlaces)}
                  onDoubleClick={() =>
                    onDoubleClickEditableString(asset, 'decimalPlaces')
                  }
                  onBlurField={onSaveEditField}
                  onEnter={onSaveEditField}
                  onEscape={() => setEditField([])}
                  isEditable={
                    !!editField.find(
                      (a) =>
                        a.id === asset.id && a.fieldType === 'decimalPlaces',
                    )
                  }
                  onlyNumber
                />
              </td>
              <td
                className={s.table__td}
              >{`${dayjs(asset.updatedAt).format('YYYY-MM-DD')} / ${dayjs(asset.createdAt).format('YYYY-MM-DD')}`}</td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Hidden input for choose SVG */}
      <input
        type="file"
        accept=".svg"
        style={{ display: 'none' }}
        ref={fileInputRef}
        onChange={handleFileChange}
      />
    </Sheet>
  );
};

export default AssetsList;
